import type { BrowserEnvironment } from "../shared/contracts";

export function detectBrowser(userAgent = navigator.userAgent): string {
  if (/Edg\//.test(userAgent)) return "Microsoft Edge";
  if (/OPR\//.test(userAgent)) return "Opera";
  if (/Firefox\//.test(userAgent)) return "Firefox";
  if (/Chrome\//.test(userAgent)) return "Chrome";
  if (/Safari\//.test(userAgent) && /Version\//.test(userAgent)) return "Safari";
  return "Unknown";
}

export function detectOs(userAgent = navigator.userAgent): string {
  if (/iPhone|iPad|iPod/.test(userAgent)) return "iOS / iPadOS";
  if (/Android/.test(userAgent)) return "Android";
  if (/Windows NT/.test(userAgent)) return "Windows";
  if (/Mac OS X/.test(userAgent)) return "macOS";
  if (/Linux/.test(userAgent)) return "Linux";
  return "Unknown";
}

export function classifyGpuRenderer(renderer: string): boolean | undefined {
  return /SwiftShader|LLVMpipe|Mesa|Software|VirtualBox|VMware|Parallels|Basic Render/i.test(
    renderer
  )
    ? true
    : undefined;
}

export function detectGpuVendor(): { gpu: string; isVirtual?: boolean } {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl || !(gl instanceof WebGLRenderingContext)) {
      return { gpu: "Unavailable" };
    }
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) {
      return { gpu: "Standard WebGL" };
    }
    const renderer = (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string) || "";
    return {
      gpu: renderer || "Unknown",
      isVirtual: renderer ? classifyGpuRenderer(renderer) : undefined
    };
  } catch {
    return { gpu: "Unavailable" };
  }
}

export function detectWebdriver(): boolean {
  return Boolean(navigator.webdriver);
}

export function collectBrowserEnvironment(): BrowserEnvironment {
  const gpuInfo = detectGpuVendor();
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
    language: navigator.language || "Unknown",
    languages: [...navigator.languages],
    browser: detectBrowser(),
    os: detectOs(),
    screen: `${window.screen.width} × ${window.screen.height}`,
    cores: navigator.hardwareConcurrency || null,
    gpu: gpuInfo.gpu,
    ...(gpuInfo.isVirtual !== undefined ? { isVirtualGpu: gpuInfo.isVirtual } : {}),
    webdriver: detectWebdriver()
  };
}
